// Services da ficha cadastral do merchant — leitura da ficha, PUT do mutável e
// CRUD granular de métodos de pagamento.
//
// NÃO há listagem/busca de propósito: o merchant é o do usuário logado
// (`authData.user.merchantId`, o mesmo uuid STO- que o backoffice espera), então
// a ficha abre direto. Procurar merchant seria pedir ao operador que escolhesse
// algo que o login já determinou.
//
// Molde: services/admin/white-labels.ts — queryOptions exportadas p/ teste,
// gcTime 0 no que é dado de configuração (nunca renderizar cache velho),
// invalidação por predicate e toasts com getErrorMessage.
import { api } from "@/api"
import { BASE_URL, ENDPOINTS } from "@/constants"
import { toast } from "@/hooks/use-toast"
import { getApiErrorMessage } from "@/lib/api-error"
import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@/libs"
import type {
  AdminMerchant,
  AdminMerchantPaymentMethod,
  AdminMerchantPaymentMethodPayload,
  AdminMerchantUpdatePayload,
  MerchantPaymentMethodName,
} from "@/types/admin-merchant"

// Chave base das queries da feature — usada também na invalidação.
export const ADMIN_MERCHANTS_QUERY_KEY =
  ENDPOINTS.ADMIN_MERCHANTS

// Substitui {{uuid}}/{{method}} no template do endpoint.
const resolvePath = (
  template: string,
  values: Record<string, string>
) =>
  Object.entries(values).reduce(
    (path, [key, value]) =>
      path.replace(`{{${key}}}`, encodeURIComponent(value)),
    template
  )

// Dado de configuração nunca deve ser servido de cache velho: o operador
// precisa ver o estado REAL antes de alterar.
const freshQueryOverrides = {
  gcTime: 0,
  staleTime: 0,
  placeholderData: undefined,
} as const

/** GET /merchants/{uuid} — ficha completa. */
export const getAdminMerchant = async (
  uuid: string
): Promise<AdminMerchant> => {
  const { data } = await api.get<AdminMerchant>(
    `${BASE_URL}${resolvePath(ENDPOINTS.ADMIN_MERCHANT, { uuid })}`
  )
  return data
}

/** GET /merchants/{uuid}/payment-methods — métodos com comissão/parcelas. */
export const getAdminMerchantPaymentMethods = async (
  uuid: string
): Promise<AdminMerchantPaymentMethod[]> => {
  const { data } = await api.get<
    AdminMerchantPaymentMethod[]
  >(
    `${BASE_URL}${resolvePath(ENDPOINTS.ADMIN_MERCHANT_PAYMENT_METHODS, { uuid })}`
  )
  // Mesma armadilha do 204: `Array.isArray` cobre "", undefined e null.
  return Array.isArray(data) ? data : []
}

export const adminMerchantQueryOptions = (
  uuid: string
) => ({
  queryKey: [ADMIN_MERCHANTS_QUERY_KEY, uuid] as const,
  queryFn: async () => getAdminMerchant(uuid),
  enabled: Boolean(uuid),
  ...freshQueryOverrides,
})

export const adminMerchantPaymentMethodsQueryOptions = (
  uuid: string
) => ({
  queryKey: [
    ADMIN_MERCHANTS_QUERY_KEY,
    "payment-methods",
    uuid,
  ] as const,
  queryFn: async () => getAdminMerchantPaymentMethods(uuid),
  enabled: Boolean(uuid),
  ...freshQueryOverrides,
})

export const useAdminMerchant = (uuid: string) =>
  useQuery(adminMerchantQueryOptions(uuid))

export const useAdminMerchantPaymentMethods = (
  uuid: string
) => useQuery(adminMerchantPaymentMethodsQueryOptions(uuid))

/** PUT /merchants/{uuid} — só o mutável; campo ausente mantém o valor atual. */
export const updateAdminMerchant = async (
  uuid: string,
  payload: AdminMerchantUpdatePayload
): Promise<AdminMerchant> => {
  const { data } = await api.put<AdminMerchant>(
    `${BASE_URL}${resolvePath(ENDPOINTS.ADMIN_MERCHANT, { uuid })}`,
    payload
  )
  return data
}

export const useUpdateAdminMerchantMutation = (
  uuid: string
) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (payload: AdminMerchantUpdatePayload) =>
      updateAdminMerchant(uuid, payload),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        predicate: (query) =>
          query.queryKey[0] === ADMIN_MERCHANTS_QUERY_KEY,
      })
    },
    onError: (error: unknown) => {
      toast({
        title: getApiErrorMessage(
          error,
          "Não foi possível salvar os dados do merchant."
        ),
        variant: "destructive",
      })
    },
  })
}

const paymentMethodsQueryKey = (uuid: string) =>
  [
    ADMIN_MERCHANTS_QUERY_KEY,
    "payment-methods",
    uuid,
  ] as const

type PaymentMethodsSnapshotContext = {
  previous?: AdminMerchantPaymentMethod[]
}

/**
 * Fábrica das mutations de método com OTIMISMO + ROLLBACK: o switch responde na
 * hora e, se o backend recusar, o cache volta ao estado anterior — sem isso o
 * operador veria o toggle "ligado" com o backend dizendo o contrário.
 */
const useOptimisticPaymentMethodsMutation = <TVariables>(
  uuid: string,
  mutationFn: (variables: TVariables) => Promise<unknown>,
  applyOptimistic: (
    cache: AdminMerchantPaymentMethod[],
    variables: TVariables
  ) => AdminMerchantPaymentMethod[],
  errorFallback: string
) => {
  const queryClient = useQueryClient()
  const queryKey = paymentMethodsQueryKey(uuid)

  return useMutation<
    unknown,
    unknown,
    TVariables,
    PaymentMethodsSnapshotContext
  >({
    mutationFn,
    onMutate: async (variables) => {
      await queryClient.cancelQueries({ queryKey })
      const previous =
        queryClient.getQueryData<
          AdminMerchantPaymentMethod[]
        >(queryKey)
      queryClient.setQueryData<
        AdminMerchantPaymentMethod[]
      >(queryKey, (cache) =>
        applyOptimistic(cache ?? [], variables)
      )
      return { previous }
    },
    onError: (error, _variables, context) => {
      queryClient.setQueryData(queryKey, context?.previous)
      toast({
        title: getApiErrorMessage(error, errorFallback),
        variant: "destructive",
      })
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({
        predicate: (query) =>
          query.queryKey[0] === ADMIN_MERCHANTS_QUERY_KEY,
      })
    },
  })
}

/** POST .../payment-methods — habilita um método novo (409 se já existe). */
export const useAddMerchantPaymentMethodMutation = (
  uuid: string
) =>
  useOptimisticPaymentMethodsMutation<AdminMerchantPaymentMethodPayload>(
    uuid,
    (payload) =>
      api.post(
        `${BASE_URL}${resolvePath(ENDPOINTS.ADMIN_MERCHANT_PAYMENT_METHODS, { uuid })}`,
        payload
      ),
    (cache, payload) => [
      ...cache,
      {
        paymentMethod:
          payload.paymentMethod as MerchantPaymentMethodName,
        active: payload.active ?? true,
        fixedCommission: payload.fixedCommission ?? null,
        percentageCommission:
          payload.percentageCommission ?? null,
        installmentCount: payload.installmentCount ?? null,
      },
    ],
    "Não foi possível habilitar o método."
  )

/** PUT .../payment-methods/{method} — comissão/parcelas e/ou active. */
export const useUpdateMerchantPaymentMethodMutation = (
  uuid: string
) =>
  useOptimisticPaymentMethodsMutation<{
    method: MerchantPaymentMethodName
    payload: AdminMerchantPaymentMethodPayload
  }>(
    uuid,
    ({ method, payload }) =>
      api.put(
        `${BASE_URL}${resolvePath(ENDPOINTS.ADMIN_MERCHANT_PAYMENT_METHOD, { uuid, method })}`,
        payload
      ),
    (cache, { method, payload }) =>
      cache.map((item) =>
        item.paymentMethod === method
          ? { ...item, ...payload }
          : item
      ),
    "Não foi possível atualizar o método."
  )

/** DELETE .../payment-methods/{method} — remove o método da loja. */
export const useRemoveMerchantPaymentMethodMutation = (
  uuid: string
) =>
  useOptimisticPaymentMethodsMutation<MerchantPaymentMethodName>(
    uuid,
    (method) =>
      api.delete(
        `${BASE_URL}${resolvePath(ENDPOINTS.ADMIN_MERCHANT_PAYMENT_METHOD, { uuid, method })}`
      ),
    (cache, method) =>
      cache.filter((item) => item.paymentMethod !== method),
    "Não foi possível remover o método."
  )
